/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'

import type { EuiSwitchEvent } from '@elastic/eui'
import { EuiFieldSearch, EuiFlexGroup, EuiFlexItem, EuiSwitch } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import useCCMClusterSearchFiltersFromQueryString from './hooks'
import messages from './messages'

import type { FC } from 'react'
import type {
  CCMClusterPageSwitchFilter,
  CCMClusterPageTextFilter,
  FilterableCCMClusterProperties,
} from './types'

const searchFieldMinWidth = {
  minWidth: '280px',
}

const DEFAULT_ENABLED_FILTERS: FilterableCCMClusterProperties[] = [
  'organizationId',
  'selfManagedClusterId',
  'licenseId',
  'includeDisconnected',
  'includeInternalUsers',
]

const ConnectedClustersFilters: FC<{
  enabledFilters?: FilterableCCMClusterProperties[]
}> = ({ enabledFilters = DEFAULT_ENABLED_FILTERS }) => {
  const { formatMessage } = useIntl()

  const { activeQueryStringFilters, setQueryStringFilters } =
    useCCMClusterSearchFiltersFromQueryString()

  const {
    organizationId,
    selfManagedClusterId,
    licenseId,
    includeDisconnected,
    includeInternalUsers,
  } = activeQueryStringFilters

  const replaceFilterValue = useCallback(
    (field: CCMClusterPageTextFilter) =>
      (query: string): void => {
        setQueryStringFilters({ ...activeQueryStringFilters, [field]: query.trim() })
      },
    [activeQueryStringFilters, setQueryStringFilters],
  )

  const toggleFilterSwitch = useCallback(
    (field: CCMClusterPageSwitchFilter) => (e: EuiSwitchEvent) => {
      setQueryStringFilters({
        ...activeQueryStringFilters,
        [field]: e.target.checked,
      })
    },
    [activeQueryStringFilters, setQueryStringFilters],
  )

  return (
    <EuiFlexGroup alignItems='center' justifyContent='spaceBetween'>
      <EuiFlexItem>
        <EuiFlexGroup justifyContent='flexStart' alignItems='center'>
          {enabledFilters.includes('organizationId') && (
            <EuiFlexItem grow={false}>
              <EuiFieldSearch
                placeholder={formatMessage(messages.searchByOrganizationId)}
                aria-label={formatMessage(messages.searchByOrganizationId)}
                defaultValue={organizationId}
                onSearch={replaceFilterValue('organizationId')}
                css={searchFieldMinWidth}
              />
            </EuiFlexItem>
          )}
          {enabledFilters.includes('selfManagedClusterId') && (
            <EuiFlexItem grow={false}>
              <EuiFieldSearch
                placeholder={formatMessage(messages.searchByClusterId)}
                aria-label={formatMessage(messages.searchByClusterId)}
                defaultValue={selfManagedClusterId}
                onSearch={replaceFilterValue('selfManagedClusterId')}
                css={searchFieldMinWidth}
              />
            </EuiFlexItem>
          )}
          {enabledFilters.includes('licenseId') && (
            <EuiFlexItem grow={false}>
              <EuiFieldSearch
                placeholder={formatMessage(messages.searchByLicenseId)}
                aria-label={formatMessage(messages.searchByLicenseId)}
                defaultValue={licenseId}
                onSearch={replaceFilterValue('licenseId')}
                css={searchFieldMinWidth}
              />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>
      {enabledFilters.includes('includeDisconnected') && (
        <EuiFlexItem grow={false}>
          <EuiSwitch
            label={<FormattedMessage {...messages.includeDisconnectedClusters} />}
            aria-label={formatMessage(messages.includeDisconnectedClusters)}
            checked={includeDisconnected}
            onChange={toggleFilterSwitch('includeDisconnected')}
          />
        </EuiFlexItem>
      )}
      {enabledFilters.includes('includeInternalUsers') && (
        <EuiFlexItem grow={false}>
          <EuiSwitch
            label={<FormattedMessage {...messages.includeInternalUsers} />}
            aria-label={formatMessage(messages.includeInternalUsers)}
            checked={includeInternalUsers}
            onChange={toggleFilterSwitch('includeInternalUsers')}
          />
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  )
}

export default ConnectedClustersFilters
